/**
 * Where the list's map view gets its basemap (KPR-392; the owner's ruling g01,
 * 2026-10-09): ONE Protomaps PMTiles file on keepr's own AWS — a private S3
 * bucket behind CloudFront — read by the browser with HTTP range requests and
 * drawn with MapLibre. No third-party tile service ever sees a visitor, and
 * openstreetmap.org's tile servers are never used.
 *
 * Two build-time variables (CRA inlines `REACT_APP_*` into the bundle):
 *
 *   REACT_APP_MAP_TILES_URL    the .pmtiles file, e.g.
 *                              https://maps.keepr.cloud/oregon-20261025.pmtiles
 *   REACT_APP_MAP_ASSETS_URL   optional: the folder holding the basemap's
 *                              fonts/ and sprites/ (protomaps/basemaps-assets);
 *                              the tiles file's own folder when unset
 *
 * Only an https address on keepr.cloud or one of its subdomains (the
 * CloudFront distribution's own domain, `maps.keepr.cloud` say) is taken, so a
 * typo or a pasted third-party URL can never put another company's server in
 * front of keepr's readers. A bare `*.cloudfront.net` address is refused too:
 * nothing about its name says the distribution is keepr's (⚑ KPR-392). A
 * development build also takes localhost. Anything else reads as UNSET, and
 * unset means the map view is not
 * offered at all (the Map segment never appears) — which is the state until the
 * owner's AWS steps in docs/RUNBOOK.md § Map tiles are done. The same rule
 * makes rollback one line: unset the variable and redeploy.
 */

const OWN_SITES = ['keepr.cloud'];
const DEV_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const isDevelopmentBuild = () => process.env.NODE_ENV !== 'production';

const onSite = (host, site) => host === site || host.endsWith(`.${site}`);

/**
 * The address as a URL string when it may serve the map, else null.
 * `development` lets http(s)://localhost through (a dev server's own tiles);
 * a production build never takes it.
 */
export function acceptedMapUrl(raw, { development = isDevelopmentBuild() } = {}) {
	const text = String(raw || '').trim();
	if (!text) return null;
	let url;
	try { url = new URL(text); } catch { return null; }
	const host = url.hostname.toLowerCase();
	if (development && DEV_HOSTS.includes(host) && (url.protocol === 'http:' || url.protocol === 'https:')) {
		return url.toString();
	}
	if (url.protocol !== 'https:') return null;
	// Credentials in a tile address would be in every reader's bundle.
	if (url.username || url.password) return null;
	return OWN_SITES.some(site => onSite(host, site)) ? url.toString() : null;
}

/** The folder a file's URL sits in, ending in `/` (query and hash dropped). */
function folderOf(href) {
	const url = new URL(href);
	const path = url.pathname.endsWith('/') ? url.pathname : url.pathname.replace(/[^/]*$/, '');
	return `${url.origin}${path}`;
}

/** A URL that names a folder, ending in `/` (query and hash dropped). */
function asFolder(href) {
	const url = new URL(href);
	const path = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
	return `${url.origin}${path}`;
}

/**
 * The map's sources, or null when the map view is not available.
 *
 *   { tilesUrl, assetsUrl, glyphs, spriteFor(flavor) }
 *
 * `glyphs` keeps MapLibre's `{fontstack}` / `{range}` placeholders as they are:
 * it is built by concatenation, never through URL(), which would encode the
 * braces.
 */
export function mapTilesConfig(env = process.env, options = {}) {
	const tilesUrl = acceptedMapUrl(env.REACT_APP_MAP_TILES_URL, options);
	if (!tilesUrl) return null;
	const assetsRaw = String(env.REACT_APP_MAP_ASSETS_URL || '').trim();
	// A set but refused assets address does not quietly fall back to the
	// tiles' folder: the person who set it meant somewhere else, and the map
	// would then ask a host nobody configured for. Off instead.
	const assetsAccepted = assetsRaw ? acceptedMapUrl(assetsRaw, options) : null;
	if (assetsRaw && !assetsAccepted) return null;
	const assetsUrl = assetsAccepted ? asFolder(assetsAccepted) : folderOf(tilesUrl);
	return {
		tilesUrl,
		assetsUrl,
		glyphs: `${assetsUrl}fonts/{fontstack}/{range}.pbf`,
		spriteFor: flavor => `${assetsUrl}sprites/v4/${flavor === 'dark' ? 'dark' : 'light'}`,
	};
}

/**
 * The credit every map carries (ODbL 1.0 requires OpenStreetMap's; Protomaps
 * made the tile schema and style). MapLibre renders it in its attribution
 * control. A constant of ours, never data, so it is safe as HTML.
 */
export const MAP_ATTRIBUTION = '<a href="https://protomaps.com" target="_blank" rel="noopener noreferrer">Protomaps</a> '
	+ '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>';

/** The configuration this build was made with. */
export const MAP_TILES = mapTilesConfig();

/** Whether this build can show a map at all. */
export const mapViewConfigured = () => Boolean(MAP_TILES);
